// types/master-data.types.ts

import { District, Mandal } from "./territorial.types";

// Enums
export enum GrantLevel {
    DISTRICT = 'DISTRICT',
    MANDAL = 'MANDAL',
    VILLAGE = 'VILLAGE'
  }
  
  export enum GrantType {
    CENTRAL = 'CENTRAL',
    STATE = 'STATE',
    COMBINED = 'COMBINED'
  }
  
  export enum WorkType {
    NEW_CONSTRUCTION = 'NEW_CONSTRUCTION',
    MAINTENANCE = 'MAINTENANCE',
    REPAIR = 'REPAIR',
    UPGRADATION = 'UPGRADATION'
  }
  
  export enum WorkStage {
    NOT_STARTED = 'NOT_STARTED',
    IN_PROGRESS = 'IN_PROGRESS',
    COMPLETED = 'COMPLETED',
    HELD_UP = 'HELD_UP'
  }
  
  export enum CategoryPST {
    P = 'P', // Primary
    S = 'S', // Secondary
    T = 'T'  // Tertiary
  }
  
  export enum MandalType {
    RURAL = 'RURAL',
    URBAN = 'URBAN'
  }
  
  export enum WorkCategory {
    ROAD = 'ROAD',
    CROSS_DRAINAGE = 'CROSS_DRAINAGE',
    BUILDINGS = 'BUILDINGS',
    REPAIRS = 'REPAIRS',
    DRAIN = 'DRAIN'
  }
  

  
  export interface WorkTypeEntity {
    work_type_id: number;
    work_type_name: string;
    work_category: WorkCategory;
    work_sub_category: string;
    is_measurable: boolean;
    unit_of_measurement: string;
    description: string;
    is_active: boolean;
    created_at?: string;
    updated_at?: string;
  }
  
  export interface Contractor {
    contractor_id: number;
    contractor_name: string;
    firm_name: string;
    pan_number: string;
    gstin?: string;
    contact_number: string;
    alternate_contact?: string;
    email: string;
    address?: string;
    city?: string;
    pincode?: string;
    state?: string;
    contractor_class?: string;
    registration_number?: string;
    registration_date?: string;
    registration_expiry?: string;
    bank_name?: string;
    account_number?: string;
    ifsc_code?: string;
    branch_name?: string;
    work_capacity?: number;
    concurrent_works_limit?: number;
    performance_rating?: number;
    specialization?: string;
    experience_years?: number;
    remarks?: string;
    is_blacklisted: boolean;
    blacklist_date?: string;
    blacklist_reason?: string;
    is_active: boolean;
    created_by?: number;
    updated_by?: number;
    created_at?: string;
    updated_at?: string;
  }
  
  // DTOs with Statistics
  export interface DistrictWithStatsDto extends District {
    mandal_count: number;
    total_works: number;
    total_estimate_cost: number;
    active_works?: number;
    completed_works?: number;
    total_expenditure?: number;
    financial_progress_percentage?: number;
    physical_progress_percentage?: number;
  }
  
  export interface MandalWithStatsDto extends Mandal {
    district_name: string;
    total_works: number;
    total_estimate_cost: number;
    active_works?: number;
    completed_works?: number;
    total_expenditure?: number;
    financial_progress_percentage?: number;
    physical_progress_percentage?: number;
    avg_work_cost?: number;
  }
  
  export interface ContractorWithStatsDto extends Contractor {
    total_works: number;
    total_contract_value: number;
    completed_works: number;
    completion_rate: number;
    in_progress_works?: number;
    avg_project_duration?: number;
    delayed_works?: number;
    on_time_completion_rate?: number;
  }
  
  // Other DTOs
  export interface SeedProgressDto {
    districts: { created: number; updated: number; errors: number };
    mandals: { created: number; updated: number; errors: number };
    workTypes: { created: number; updated: number; errors: number };
    contractors: { created: number; updated: number; errors: number };
    totalTime: number;
  }
  
  export interface ValidationResponseDto {
    valid?: boolean;
    active?: boolean;
    message: string;
  }
  
  export interface SearchResultsDto {
    districts: District[];
    mandals: Mandal[];
    contractors: Contractor[];
    workTypes: WorkTypeEntity[];
  }
  
  export interface MasterDataStatsDto {
    districts: {
      total: number;
      active: number;
      inactive: number;
    };
    mandals: {
      total: number;
      active: number;
      inactive: number;
    };
    workTypes: {
      total: number;
      active: number;
      inactive: number;
      byCategory: Record<string, number>;
    };
    contractors: {
      total: number;
      active: number;
      inactive: number;
    };
  }
  
  export interface HealthCheckDto {
    status: 'healthy' | 'degraded' | 'unhealthy';
    checks: {
      database: boolean;
      districts: boolean;
      mandals: boolean;
      workTypes: boolean;
      contractors: boolean;
    };
    timestamp: Date;
  }
  
  export interface ExportDataDto {
    districts: District[];
    mandals: Mandal[];
    workTypes: WorkTypeEntity[];
    contractors: Contractor[];
    exportDate: Date;
    totalRecords: number;
  }
  
  export interface DistrictMandalSeedDto {
    district_code: string;
    mandals: Array<{
      mandal_code: string;
      mandal_name: string;
      mandal_type: MandalType;
    }>;
  }
  
  // Dropdown Values Interface
  export interface DropdownValuesDto {
    grantLevels: GrantLevel[];
    grantTypes: GrantType[];
    workTypes: WorkType[];
    workStages: WorkStage[];
    categories: CategoryPST[];
    mandalTypes: MandalType[];
    workCategories: WorkCategory[];
    subStages: {
      [WorkStage.NOT_STARTED]: string[];
      [WorkStage.IN_PROGRESS]: {
        'New BT Road': string[];
        'Buildings': string[];
        'CC Road / Side Drain': string[];
        'Bridge': string[];
      };
      [WorkStage.COMPLETED]: string[];
      [WorkStage.HELD_UP]: string[];
    };
  }
  
  // API Response Types
  export interface ApiResponse<T> {
    data?: T;
    message?: string;
    status?: number;
  }
  
  export interface PaginatedResponse<T> {
    data: T[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
  }
  
  // Request Types
  export interface SearchParams {
    term: string;
    category?: string;
    page?: number;
    limit?: number;
  }
  
  export interface FilterParams {
    districtId?: number;
    category?: string;
    isActive?: boolean;
    page?: number;
    limit?: number;
  }